import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/lib/store";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { UserPlus, KeyRound, Trash2, Search, Pencil, UserCog } from "lucide-react";

const ROLES = ["customer", "wholesale", "partner", "manager", "admin", "super_admin"];
const ADMIN_ROLES = ["super_admin", "admin", "manager"];

const roleColor = (r) =>
  r === "super_admin" ? "bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-500/30" :
  r === "admin" ? "bg-cyan-500/20 text-cyan-300 border-cyan-500/30" :
  r === "manager" ? "bg-sky-500/20 text-sky-300 border-sky-500/30" :
  r === "wholesale" ? "bg-amber-500/20 text-amber-300 border-amber-500/30" :
  r === "partner" ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" :
  "bg-slate-500/20 text-slate-300 border-slate-500/30";

const Fld = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[11px] font-mono uppercase tracking-widest text-slate-400">{label}</Label>
    {children}
  </div>
);

const RoleSelect = ({ value, onChange, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid} className="bg-black/30 border-white/10 text-white">
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      {ROLES.map((r) => (
        <SelectItem key={r} value={r} data-testid={`role-opt-${r}`}>{r.replace(/_/g, " ")}</SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const CreateUserDialog = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ email: "", name: "", password: "", role: "customer", wholesale_tier: "", reward_points: 0 });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const reset = () => setF({ email: "", name: "", password: "", role: "customer", wholesale_tier: "", reward_points: 0 });

  const submit = async () => {
    if (!f.email || !f.password) return toast.error("Email and password are required");
    setSaving(true);
    try {
      await api.post("/admin/users", { ...f, reward_points: Number(f.reward_points) || 0, wholesale_tier: f.wholesale_tier || null });
      toast.success("User created");
      setOpen(false);
      reset();
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create user");
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button className="btn-primary rounded-lg" data-testid="add-user-btn"><UserPlus className="w-4 h-4 mr-2" />Add User</Button>
      </DialogTrigger>
      <DialogContent className="bg-[#0a0d12] border-white/10 text-white sm:max-w-md" data-testid="create-user-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl">New User</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <Fld label="Email"><Input data-testid="create-email" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} className="bg-black/30 border-white/10" /></Fld>
          <Fld label="Name"><Input data-testid="create-name" value={f.name} onChange={(e) => set("name", e.target.value)} className="bg-black/30 border-white/10" /></Fld>
          <Fld label="Password"><Input data-testid="create-password" type="password" value={f.password} onChange={(e) => set("password", e.target.value)} className="bg-black/30 border-white/10" placeholder="min 6 characters" /></Fld>
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Role"><RoleSelect value={f.role} onChange={(v) => set("role", v)} testid="create-role" /></Fld>
            <Fld label="Reward Points"><Input data-testid="create-points" type="number" value={f.reward_points} onChange={(e) => set("reward_points", e.target.value)} className="bg-black/30 border-white/10" /></Fld>
          </div>
          <Fld label="Wholesale Tier (optional)"><Input data-testid="create-tier" value={f.wholesale_tier} onChange={(e) => set("wholesale_tier", e.target.value)} className="bg-black/30 border-white/10" placeholder="e.g. WHOLESALE_1" /></Fld>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} className="btn-primary rounded-lg w-full" data-testid="create-user-submit">{saving ? "Creating…" : "Create User"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const EditUserSheet = ({ user, onClose, onSaved, onSwap }) => {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState("");

  useEffect(() => {
    if (user) setF({
      name: user.name || "", email: user.email || "", role: user.role || "customer",
      wholesale_tier: user.wholesale_tier || "", partner_code: user.partner_code || "",
      reward_points: user.reward_points ?? 0,
    });
    setPw("");
  }, [user]);

  if (!user || !f) return null;
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const isAdminRow = ADMIN_ROLES.includes(user.role);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/users/${user.id}`, { ...f, reward_points: Number(f.reward_points) || 0, wholesale_tier: f.wholesale_tier, partner_code: f.partner_code });
      toast.success("User updated");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
    setSaving(false);
  };

  const resetPw = async () => {
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    try {
      await api.post(`/admin/users/${user.id}/password`, { password: pw });
      toast.success("Password reset");
      setPw("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    }
  };

  const del = async () => {
    try {
      await api.delete(`/admin/users/${user.id}`);
      toast.success("User deleted");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <Sheet open={!!user} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="bg-[#0a0d12] border-white/10 text-white w-full sm:max-w-md overflow-y-auto" data-testid="edit-user-sheet">
        <SheetHeader><SheetTitle className="font-serif text-2xl text-white flex items-center gap-2"><UserCog className="w-5 h-5 text-cyan-300" />Edit User</SheetTitle></SheetHeader>
        <div className="mt-6 space-y-4">
          <Fld label="Name"><Input data-testid="edit-name" value={f.name} onChange={(e) => set("name", e.target.value)} className="bg-black/30 border-white/10" /></Fld>
          <Fld label="Email"><Input data-testid="edit-email" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} className="bg-black/30 border-white/10" /></Fld>
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Role"><RoleSelect value={f.role} onChange={(v) => set("role", v)} testid="edit-role" /></Fld>
            <Fld label="Reward Points"><Input data-testid="edit-points" type="number" value={f.reward_points} onChange={(e) => set("reward_points", e.target.value)} className="bg-black/30 border-white/10" /></Fld>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Wholesale Tier"><Input data-testid="edit-tier" value={f.wholesale_tier} onChange={(e) => set("wholesale_tier", e.target.value)} className="bg-black/30 border-white/10" /></Fld>
            <Fld label="Partner Code"><Input data-testid="edit-partner-code" value={f.partner_code} onChange={(e) => set("partner_code", e.target.value)} className="bg-black/30 border-white/10" /></Fld>
          </div>
          <Button onClick={save} disabled={saving} className="btn-primary rounded-lg w-full" data-testid="save-user-btn">{saving ? "Saving…" : "Save Changes"}</Button>

          <div className="pt-4 border-t border-white/10 space-y-2">
            <Label className="text-[11px] font-mono uppercase tracking-widest text-slate-400 flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5" />Reset Password</Label>
            <div className="flex gap-2">
              <Input data-testid="reset-password-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password" className="bg-black/30 border-white/10" />
              <Button onClick={resetPw} className="btn-ghost rounded-lg shrink-0" data-testid="reset-password-btn">Set</Button>
            </div>
          </div>

          <div className="pt-4 border-t border-white/10 flex gap-2">
            {!isAdminRow && (
              <Button onClick={() => onSwap(user.id)} className="btn-ghost rounded-lg flex-1" data-testid="swap-user-btn">Swap as user</Button>
            )}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" className="rounded-lg flex-1 bg-red-500/20 text-red-300 border border-red-500/30 hover:bg-red-500/30" data-testid="delete-user-btn"><Trash2 className="w-4 h-4 mr-2" />Delete</Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-[#0a0d12] border-white/10 text-white">
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete {user.email}?</AlertDialogTitle>
                  <AlertDialogDescription className="text-slate-400">This permanently removes the user account. This cannot be undone.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="bg-transparent border-white/10 text-white">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={del} className="bg-red-500/80 hover:bg-red-500" data-testid="confirm-delete-user">Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default function UsersManager() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const { impersonate } = useApp();
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/data/users"); setRows(data); } catch { setRows([]); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const swap = async (id) => {
    try { navigate("/my-lab"); await impersonate(id); toast.success("Now viewing as this user"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not swap"); }
  };

  const filtered = rows.filter((r) => {
    const s = q.toLowerCase();
    return !s || (r.email || "").toLowerCase().includes(s) || (r.name || "").toLowerCase().includes(s) || (r.role || "").includes(s);
  });

  return (
    <div data-testid="users-manager">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h1 className="font-serif text-4xl text-white">Users</h1>
        <CreateUserDialog onCreated={load} />
      </div>

      <div className="relative mb-4 max-w-sm">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <Input data-testid="user-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email or role" className="pl-9 bg-black/30 border-white/10 text-white" />
      </div>

      {loading && <div className="text-slate-500 font-mono text-sm animate-pulse">Loading…</div>}
      {!loading && filtered.length === 0 && <div className="glass rounded-2xl p-10 text-center text-slate-500 text-sm">No users found.</div>}
      {!loading && filtered.length > 0 && (
        <div className="glass rounded-2xl overflow-hidden"><div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-white/10">
              {["Name", "Email", "Role", "Tier", "Points", ""].map((c) => (
                <th key={c} className="p-3 text-left text-[10px] font-mono uppercase tracking-widest text-slate-500">{c}</th>
              ))}
            </tr></thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer" data-testid={`user-row-${r.id}`} onClick={() => setEditing(r)}>
                  <td className="p-3 text-slate-200">{r.name || "—"}</td>
                  <td className="p-3 font-mono text-xs text-slate-300 max-w-[220px] truncate">{r.email}</td>
                  <td className="p-3"><Badge className={`border ${roleColor(r.role)} text-[10px] uppercase tracking-wide`}>{(r.role || "").replace(/_/g, " ")}</Badge></td>
                  <td className="p-3 font-mono text-xs text-slate-400">{r.wholesale_tier || "—"}</td>
                  <td className="p-3 font-mono text-xs text-cyan-300">{r.reward_points ?? 0}</td>
                  <td className="p-3 text-right">
                    <Button size="sm" className="btn-ghost rounded-lg text-xs" data-testid={`edit-user-${r.id}`} onClick={(e) => { e.stopPropagation(); setEditing(r); }}>
                      <Pencil className="w-3.5 h-3.5 mr-1.5" />Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div></div>
      )}

      <EditUserSheet user={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} onSwap={swap} />
    </div>
  );
}
